import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  Pagination,
  Switch,
} from '@ads/components-react';
import { CheckIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  QuestionTypeFilter,
  RevisionExamDetail,
  RevisionLang,
  RevisionTranslationQuestion,
} from '@/features/exam-revision/types/revision';
import {
  questionFilterI18nKey,
  questionTypeI18nKey,
} from '@/features/exam-revision/utils/questionType';
import { RichTextEditor, Spinner } from '@/shared/ui/atoms';

function ColumnTag({ children }: { children: ReactNode }): ReactNode {
  return (
    <span className="inline-flex w-fit items-center rounded-sm bg-ctx-interactive-primary px-nano py-quark ts-heading-xxs text-ctx-interactive-primaryAlternative">
      {children}
    </span>
  );
}

/**
 * Props do template de tradução de questões de um idioma da prova.
 */
export interface RevisionTranslationTemplateProps {
  /** Detalhes do exame em tradução (ID, título, disciplina, data) */
  readonly exam: RevisionExamDetail;
  /** Idioma de destino da tradução */
  readonly lang: RevisionLang;
  /** Questão atualmente em foco para edição */
  readonly current: RevisionTranslationQuestion | null;
  /** Índice (0-based) da questão atual dentro da lista filtrada */
  readonly currentIndex: number;
  /** Total de questões no filtro ativo (navegação/paginação) */
  readonly total: number;
  /** Total de questões da prova, sem filtro (para o rótulo "Questão X de Y") */
  readonly examTotal: number;
  /** Filtro de tipo de questão ativo */
  readonly typeFilter: QuestionTypeFilter;
  /** Opções de filtro a exibir ("Todos" + tipos presentes na prova) */
  readonly availableFilters: readonly QuestionTypeFilter[];
  /** Callback ao selecionar um filtro de tipo */
  readonly onTypeFilter: (type: QuestionTypeFilter) => void;
  /** Callback ao navegar para um índice específico */
  readonly onNavigate: (index: number) => void;
  /** Callback ao atualizar campos de uma questão de tradução (apenas local) */
  readonly onUpdate: (id: number, patch: Partial<RevisionTranslationQuestion>) => void;
  /** Callback ao salvar a tradução da questão em foco (`PUT /question`) */
  readonly onSave: () => void;
  /** Callback ao finalizar a tradução */
  readonly onFinalize: () => void;
  /** Sinaliza que a tradução da questão em foco está sendo salva */
  readonly isSaving?: boolean;
  /** Sinaliza que a finalização está em andamento */
  readonly isFinishing?: boolean;
  /** Indica se os dados ainda estão carregando */
  readonly isLoading: boolean;
  /** Sinaliza falha ao carregar a tradução */
  readonly isError?: boolean;
  /** Recarrega a tradução após erro */
  readonly onRetry?: () => void;
}

/**
 * Template da tela de tradução de questões de um idioma da prova.
 *
 * Replica o protótipo "Tradução de Questões": cabeçalho com breadcrumb completo
 * e subtítulo, pills de filtro por tipo, barra de navegação da questão, card de
 * comparação lado a lado (Original PT-BR vs. Tradução) com toolbar de revisão,
 * card de alternativas com badge "Opção N" e editor de tradução, e rodapé com
 * paginação centralizada e ação de finalizar.
 *
 * @param exam - Detalhes do exame
 * @param lang - Idioma de destino da tradução
 * @param current - Questão em foco
 * @param currentIndex - Índice atual (0-based)
 * @param total - Total de questões no filtro ativo
 * @param typeFilter - Filtro de tipo selecionado
 * @param onTypeFilter - Altera o filtro de tipo
 * @param onNavigate - Navega para um índice específico
 * @param onUpdate - Atualiza campos da questão de tradução atual (apenas local)
 * @param onFinalize - Finaliza a tradução
 * @param isFinishing - Sinaliza finalização em andamento
 * @param isLoading - Estado de carregamento
 * @param isError - Sinaliza falha ao carregar
 * @param onRetry - Recarrega a tradução após erro
 *
 * @example
 * ```tsx
 * <RevisionTranslationTemplate
 *   exam={exam}
 *   lang={lang}
 *   current={current}
 *   currentIndex={0}
 *   total={36}
 *   typeFilter="Todos"
 *   onTypeFilter={setTypeFilter}
 *   onNavigate={goTo}
 *   onUpdate={updateTranslation}
 *   onFinalize={handleFinalize}
 *   isLoading={false}
 * />
 * ```
 */
export function RevisionTranslationTemplate({
  exam,
  lang,
  current,
  currentIndex,
  total,
  examTotal,
  typeFilter,
  availableFilters,
  onTypeFilter,
  onNavigate,
  onUpdate,
  onSave,
  onFinalize,
  isSaving = false,
  isFinishing = false,
  isLoading,
  isError = false,
  onRetry,
}: RevisionTranslationTemplateProps) {
  const { t } = useTranslation('examRevision');

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-nano py-xl">
        <span className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('translation.error')}
        </span>
        {onRetry && (
          <Button hierarchy="secondary" size="sm" onClick={onRetry}>
            {t('translation.retry')}
          </Button>
        )}
      </div>
    );
  }

  if (isLoading || !current) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Spinner size="md" />
      </div>
    );
  }

  const questionTypeKey = questionTypeI18nKey(current.questionType);

  return (
    <div className="flex flex-col gap-mini">
      {/* Page Header */}
      <div className="flex flex-col gap-nano">
        <Breadcrumb>
          <BreadcrumbLink>
            <Link to="/">{t('breadcrumb.home')}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink>
            <Link to={'/exams' as never}>{t('breadcrumb.exams')}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink>
            <Link to={'/exam/revision' as never}>{t('breadcrumb.revision')}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink>
            <Link to={`/exam/revision/${exam.id}/languages` as never}>{exam.title}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink>
            <Link to={`/exam/revision/${exam.id}/languages` as never}>
              {t('breadcrumb.languages')}
            </Link>
          </BreadcrumbLink>
          <BreadcrumbPage>
            {t('translation.title', { language: lang.cultureInfoDisplay })}
          </BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-col gap-quark">
          <h1 className="ts-heading-lg text-ctx-content-title">
            {t('translation.title', { language: lang.cultureInfoDisplay })}
          </h1>
          <p className="ts-paragraph-sm text-ctx-content-placeholder">
            {t('translation.subtitle', {
              examTitle: exam.title,
              cultureInfo: lang.cultureInfo,
            })}
          </p>
        </div>
      </div>

      {/* Type Filter Pills */}
      <div className="flex flex-wrap items-center gap-nano">
        <span className="text-xxs font-bold text-ctx-content-base">
          {t('translation.filterLabel')}
        </span>
        {availableFilters.map(filter => (
          <button
            key={String(filter)}
            type="button"
            onClick={() => onTypeFilter(filter)}
            className={[
              'rounded-pill px-micro py-quark text-xxs transition-colors',
              typeFilter === filter
                ? 'bg-ctx-interactive-primary font-medium text-ctx-interactive-primaryAlternative'
                : 'border-hairline border-base-gray-300 bg-ctx-layout-surface text-ctx-content-base hover:bg-base-gray-100',
            ].join(' ')}
          >
            {t(questionFilterI18nKey(filter))}
          </button>
        ))}
      </div>

      {/* Question Nav Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-nano">
          <ListBulletIcon className="h-xs w-xs text-ctx-content-base" />
          <span className="ts-heading-xs text-ctx-content-title">
            {t('translation.questionOf', { current: current.index, total: examTotal })}
          </span>
          <span className="rounded-pill bg-base-gray-200 px-nano py-quark text-xxxs font-medium text-base-gray-700">
            {t(questionTypeKey)}
          </span>
        </div>
        <div className="flex items-center gap-nano">
          <Button
            hierarchy="secondary"
            size="sm"
            disabled={currentIndex === 0}
            onClick={() => onNavigate(currentIndex - 1)}
          >
            {t('translation.previous')}
          </Button>
          <Button
            hierarchy="primary"
            size="sm"
            disabled={currentIndex >= total - 1}
            onClick={() => onNavigate(currentIndex + 1)}
          >
            {t('translation.next')}
          </Button>
        </div>
      </div>

      {/* Comparison Card */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface">
        {/* Toolbar */}
        <div className="flex items-center justify-end gap-mini border-b-hairline border-ctx-layout-border bg-base-gray-100 px-xs py-micro">
          <div className="flex items-center gap-nano">
            <span className="ts-subtitle-xxs text-ctx-content-base">
              {t('translation.toolbar.reviewed')}
            </span>
            <Switch
              checked={current.isReviewed}
              onCheckedChange={checked => onUpdate(current.id, { isReviewed: Boolean(checked) })}
              size="sm"
            />
            <span
              className={
                current.isReviewed
                  ? 'ts-paragraph-xs text-ctx-content-title'
                  : 'ts-paragraph-xs text-ctx-content-placeholder'
              }
            >
              {current.isReviewed
                ? t('translation.toolbar.reviewedYes')
                : t('translation.toolbar.reviewedNo')}
            </span>
          </div>
          <span className="ts-caption-xxs max-w-[22rem] text-ctx-content-placeholder">
            {t('translation.toolbar.saveHint')}
          </span>
          <Button
            hierarchy="secondary"
            size="sm"
            leadingIcon={CheckIcon}
            onClick={onSave}
            isLoading={isSaving}
          >
            {t('translation.toolbar.save')}
          </Button>
        </div>

        {/* Side-by-side body — statement */}
        <div className="grid grid-cols-2">
          <div className="flex flex-col gap-mini border-r-hairline border-ctx-layout-border p-xs">
            <ColumnTag>{t('translation.columns.originalTag')}</ColumnTag>
            <div className="flex flex-col gap-nano">
              <span className="ts-subtitle-xxs text-ctx-content-title">
                {t('review.sections.label')}
              </span>
              <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-micro">
                <div
                  className="ts-paragraph-xs text-ctx-content-base"
                  dangerouslySetInnerHTML={{ __html: current.originalLabel }}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-mini p-xs">
            <ColumnTag>
              {t('translation.columns.translationTag', {
                culture: lang.cultureInfo,
              })}
            </ColumnTag>
            <div className="flex flex-col gap-nano">
              <span className="ts-subtitle-xxs text-ctx-content-title">
                {t('review.sections.label')}
              </span>
              <RichTextEditor
                key={current.id}
                value={current.translatedLabel}
                onChange={v => onUpdate(current.id, { translatedLabel: v })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Alternatives Card */}
      <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
        <span className="ts-heading-sm text-ctx-content-title">
          {t('translation.alternatives')}
        </span>
        {current.alternatives.map((alt, idx) => (
          <div
            key={`${current.id}-${alt.id}`}
            className="grid grid-cols-2 gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-micro"
          >
            <div className="flex flex-col gap-nano">
              <span className="w-fit rounded-pill bg-base-gray-200 px-nano py-quark text-xxxs font-medium text-base-gray-700">
                {t('translation.optionLabel', { number: idx + 1 })}
              </span>
              <p className="ts-paragraph-xs text-ctx-content-base">{alt.originalText}</p>
            </div>
            <RichTextEditor
              value={alt.translatedText}
              onChange={v => {
                onUpdate(current.id, {
                  alternatives: current.alternatives.map(a =>
                    a.id === alt.id ? { ...a, translatedText: v } : a,
                  ),
                });
              }}
            />
          </div>
        ))}
      </div>

      {/* Footer with Pagination */}
      <div className="flex items-center gap-mini">
        <div className="flex-1" />
        <Pagination
          pageAmount={total}
          activeIndex={currentIndex}
          setActiveIndex={v => onNavigate(typeof v === 'function' ? v(currentIndex) : v)}
          ellipsisLabel="…"
        />
        <div className="flex flex-1 justify-end">
          <Button
            hierarchy="primary"
            size="md"
            leadingIcon={CheckIcon}
            onClick={onFinalize}
            isLoading={isFinishing}
          >
            {t('translation.footer.finalize')}
          </Button>
        </div>
      </div>
    </div>
  );
}
